import { endsWith, find, isSafeInteger, startsWith } from "@1js/functional";

/* This module implements parsing and evaluation of strings with a special
syntax for pluralization. The strings can contain multiple clauses, and each
clause contains a list of ranges and an associated value. Given a number, the
resulting "pluralized string" should be the value that's associated with the
first range that matches the number.

For example, an input string to be parsed could be: "1::One minute||2::Two
minutes||3::Three minutes||4->INF::{0} minutes"

Given the input number 3, getPluralizedString would return "Three minutes".

The parseRange function deals with parsing of a single range, and returns a
function to decide whether a number matches the parsed range. The
parsePluralizedString function parses clauses into ranges and values and keeps
invoking a callback function for each range. The getPluralizedString function
uses parsePluralizedString, and exits early if we find a matching range. The
validatePluralizedString function is similar, but makes sure to parse the entire
string, to fully check the syntax.
*/

const CLAUSE_SEPARATOR = "||";
const VALUE_SEPARATOR = "::";
const RANGE_SEPARATOR = ",";
const BOUNDS_SEPARATORS = ["->", "–>", "-", "–"];
const INFINITE = "INF";
const WILDCARD = "*";
const DIGITS = /^\d+$/;

type RangePredicate = (count: number) => boolean;

export function parseRange(range: string): RangePredicate | null {
  // We allow various bounds separators; use the first one that occurs
  const sep = find(BOUNDS_SEPARATORS, (sep) => range.indexOf(sep) >= 0);
  const bounds = !sep ? [range.trim()] : range.split(sep).map((b) => b.trim());

  if (bounds.length === 1) {
    const num = bounds[0];
    if (startsWith(num, WILDCARD)) {
      const suffix = num.substring(WILDCARD.length);
      if (suffix.match(DIGITS)) {
        return (count) => endsWith(count.toString(), suffix);
      } else {
        return null;
      }
    } else {
      if (num.match(DIGITS)) {
        return (count) => count.toString() === num;
      } else {
        return null;
      }
    }
  } else if (bounds.length === 2) {
    if (!bounds[0].match(DIGITS)) {
      return null;
    }

    const lowerBound = parseInt(bounds[0], 10);
    if (isNaN(lowerBound)) {
      return null;
    }

    if (bounds[1] === INFINITE) {
      return (count) => count >= lowerBound;
    } else if (!bounds[1].match(DIGITS)) {
      return null;
    }

    const upperBound = parseInt(bounds[1], 10);
    if (isNaN(upperBound)) {
      return null;
    }

    return (count) => count >= lowerBound && count < upperBound;
  } else {
    return null;
  }
}

/**
 * @deprecated please use parsePluralString from ./ICEPluralization instead
 */
export function parsePluralizedString(
  formatString: string,
  callback: (value: string, predicate: RangePredicate) => boolean
): void {
  const throwError = (message: string) => {
    throw new Error(`pluralized string "${formatString}" ${message}`);
  };

  for (const clause of formatString.split(CLAUSE_SEPARATOR)) {
    const rangesAndValue = clause.split(VALUE_SEPARATOR);

    if (rangesAndValue.length !== 2) {
      throwError(
        `is missing separator ${VALUE_SEPARATOR} in clause "${clause}"`
      );
    }

    const [ranges, value] = rangesAndValue;

    for (const range of ranges.split(RANGE_SEPARATOR)) {
      const predicate = parseRange(range);
      if (predicate === null) {
        throwError(`contains invalid range "${range}"`);
      } else if (!callback(value, predicate)) {
        return;
      }
    }
  }
}

/**
 * @deprecated please use getPluralStringUnsafe from ./ICEPluralization instead
 */
export function getPluralizedStringUnsafe(
  formatString: string,
  count: number
): string {
  if (count < 0 || (count !== Infinity && !isSafeInteger(count))) {
    throw new Error("count must be a positive integer or Infinity");
  }

  let result = "";

  // Parse the string and break once we find a matching range
  parsePluralizedString(formatString, (value, predicate) => {
    if (predicate(count)) {
      result = value;
      return false; // stop parsing
    }

    return true; // continue parsing
  });

  return result;
}

/**
 * @deprecated please use getPluralString from ./ICEPluralization instead
 */
export function getPluralizedString(
  formatString: string,
  count: number | undefined
): string {
  if (count === undefined) {
    return formatString;
  }

  try {
    return getPluralizedStringUnsafe(formatString, count);
  } catch (_ex) {
    return formatString;
  }
}

export function validatePluralizedString(formatString: string): void {
  // Parse all of the string and throw an error if it's not valid
  parsePluralizedString(formatString, () => true);
}
